import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

export default function PortalManagement() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ portalId: '', name: '', domain: '', status: 'active', settings: { maxUsers: 50, maxCampaigns: 20, features: [] } });
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [portalStats, setPortalStats] = useState(null);

  const fetchPortals = () => {
    api.get('/superadmin/portals').then(({ data }) => { setPortals(data.portals || []); setLoading(false); })
      .catch(() => { toast.error('Failed to load portals'); setLoading(false); });
  };

  useEffect(() => { fetchPortals(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ portalId: '', name: '', domain: '', status: 'active', settings: { maxUsers: 50, maxCampaigns: 20, features: [] } });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    const s = p.settings || {};
    setForm({ portalId: p.portalId, name: p.name, domain: p.domain || '', status: p.status, settings: { maxUsers: s.maxUsers || 50, maxCampaigns: s.maxCampaigns || 20, features: Array.isArray(s.features) ? s.features : [] } });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/superadmin/portals/${editing._id}`, form);
        toast.success('Portal updated');
      } else {
        await api.post('/superadmin/portals', form);
        toast.success('Portal created');
      }
      setShowModal(false); fetchPortals();
    } catch (err) { toast.error(err.response?.data?.msg || 'Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this portal? This cannot be undone.')) return;
    try {
      await api.delete(`/superadmin/portals/${id}`);
      toast.success('Portal deleted');
      fetchPortals();
    } catch { toast.error('Delete failed'); }
  };

  const viewStats = async (p) => {
    try {
      const { data } = await api.get(`/superadmin/portals/${p._id}/stats`);
      setPortalStats(data); setSelectedPortal(p);
    } catch { toast.error('Failed to load stats'); }
  };

  const featureOptions = ['sms', 'whatsapp', 'whatsapp_image', 'status_messaging', 'newsletter', 'export', 'reports'];

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Portal Management</h1>
          <p className="page-subtitle">Manage all marketing portals</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          + New Portal
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
        {portals.map(p => (
          <div key={p._id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 600 }}>{p.name}</h3>
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>ID: {p.portalId}</span>
              </div>
              <span className={`badge badge-${p.status}`}>{p.status}</span>
            </div>
            {p.domain && <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>🌐 {p.domain}</div>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {(Array.isArray(p.settings?.features) ? p.settings.features : []).map(f => (
                <span key={f} className="badge" style={{ background: 'rgba(126,165,46,0.08)', color: '#7ea52e', fontSize: 11 }}>{f}</span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary" style={{ flex: 1, fontSize: 12 }} onClick={() => viewStats(p)}>Stats</button>
              <button className="btn btn-secondary" style={{ flex: 1, fontSize: 12 }} onClick={() => openEdit(p)}>Edit</button>
              <button className="btn btn-secondary" style={{ flex: 1, fontSize: 12, color: '#ef4444' }} onClick={() => handleDelete(p._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Stats Modal */}
      {selectedPortal && portalStats && (
        <div className="modal-overlay" onClick={() => setSelectedPortal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedPortal.name} - Stats</h2>
              <button className="modal-close" onClick={() => setSelectedPortal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="stat-card"><div className="stat-card-value">{portalStats.users || 0}</div><div className="stat-card-label">Users</div></div>
                <div className="stat-card"><div className="stat-card-value">{portalStats.campaigns || 0}</div><div className="stat-card-label">Campaigns</div></div>
                <div className="stat-card"><div className="stat-card-value">{(portalStats.masterData || 0).toLocaleString()}</div><div className="stat-card-label">Master Data</div></div>
                <div className="stat-card"><div className="stat-card-value">{(portalStats.sendLogs || 0).toLocaleString()}</div><div className="stat-card-label">Send Logs</div></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2 className="modal-title">{editing ? 'Edit Portal' : 'New Portal'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Portal ID</label>
                  <input type="text" className="form-input" value={form.portalId}
                    onChange={e => setForm({ ...form, portalId: e.target.value })} required disabled={!!editing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input type="text" className="form-input" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Domain</label>
                  <input type="text" className="form-input" value={form.domain}
                    onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="example.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Max Users</label>
                    <input type="number" className="form-input" value={form.settings.maxUsers}
                      onChange={e => setForm({ ...form, settings: { ...form.settings, maxUsers: +e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Campaigns</label>
                    <input type="number" className="form-input" value={form.settings.maxCampaigns}
                      onChange={e => setForm({ ...form, settings: { ...form.settings, maxCampaigns: +e.target.value } })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Features</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {featureOptions.map(f => (
                      <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={(Array.isArray(form.settings.features) ? form.settings.features : []).includes(f)}
                          onChange={(e) => {
                            const curr = Array.isArray(form.settings.features) ? form.settings.features : [];
                            const features = e.target.checked
                              ? [...curr, f]
                              : curr.filter(x => x !== f);
                            setForm({ ...form, settings: { ...form.settings, features } });
                          }} />
                        {f}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
